import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

export interface User {
  id: number;
  user_type_id: number | null;
  location_id: number | null;
  name: string;
  email: string;
  phone: string;
  status: number;
  created_at: string;
  date_of_birth?: string;
  gender?: "male" | "female"; // Updated to be a union type
  country?: string;
  state?: string;
  lga?: string;
  address?: string;
  whatsapp_phone?: string;
  avatar?: string; // Added avatar property
  roles?: {
    id: number;
    name: string;
    description?: string;
  };
  user_type?: {
    id: number;
    name: string;
    description?: string;
    icon?: string;
  };
  location?: {
    id: number;
    name: string;
    address?: string;
    code?: string;
  };
}

// Define guest user type ID constant
export const GUEST_USER_TYPE_ID = 3; // Guest user type ID is 3 based on your data

export const useUsers = (page: number, filters: Record<string, any> = {}) => {
  // Create a copy of filters for API call
  const apiFilters = { ...filters };

  // Check if we should include guests
  const includeGuests = apiFilters.include_guests === true;

  // Remove the include_guests flag from API filters as it's not a real API parameter
  if ("include_guests" in apiFilters) {
    delete apiFilters.include_guests;
  }

  // Don't add any special filtering for guests at the API level
  // We'll filter them out client-side after getting results

  return useQuery({
    queryKey: ["users", page, filters, includeGuests], // Include includeGuests in the query key
    queryFn: async () => {
      // Log what we're sending to the API for debugging
      console.log("Fetching users with params:", apiFilters);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...Object.fromEntries(
          Object.entries(apiFilters).filter(([_, v]) => v !== null && v !== "")
        ), // Exclude null/empty filters
      }).toString();

      const response = await apiClient.get<{
        data: User[];
        meta: { total: number; current_page: number; last_page: number };
      }>(`/user?${queryParams}`);

      // If we're including guests or filtering by a specific user type, return as is
      if (includeGuests || apiFilters.user_type_id) {
        return response.data;
      }

      // Otherwise, filter out guests client-side
      const filteredData = response.data.data.filter(
        (user) => user.user_type_id !== GUEST_USER_TYPE_ID
      );

      // Create a new response object with adjusted meta information
      return {
        data: filteredData,
        meta: {
          ...response.data.meta,
          // Adjust total count to reflect filtered results
          total:
            response.data.meta.total -
            (response.data.data.length - filteredData.length),
        },
      };
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Helper function to check for specific error messages
const checkForDuplicateEmail = (error: any) => {
  // Check if the error response contains a duplicate email message
  if (error?.response?.data?.message) {
    const errorMessage = error.response.data.message.toLowerCase();
    if (
      (errorMessage.includes("duplicate") && errorMessage.includes("email")) ||
      errorMessage.includes("users_email_unique")
    ) {
      return true;
    }
  }
  return false;
};

// ✅ Mutation for Creating a User
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: {
      name: string;
      email: string;
      password: string;
      user_type_id: number;
      role_id?: number;
      location_id: number;
      date_of_birth?: string;
      gender: string;
      country: string;
      state: string;
      lga: string;
      address?: string;
      phone: string;
      whatsapp_phone?: string;
      avatar?: string;
      status: boolean;
    }) => {
      // Make sure role_id is not undefined or null
      if (!userData.role_id) {
        throw new Error("Role ID is required");
      }

      // Ensure role_id is a number
      const payload = {
        ...userData,
        role_id: Number(userData.role_id),
      };

      console.log("Creating user with payload:", payload); // For debugging

      const response = await apiClient.post("/user/create", payload);
      return response.data;
    },
    onSuccess: () => {
      message.success("User created successfully!");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      console.error("User creation error:", error);

      // Check for role_id errors
      if (error?.message === "Role ID is required") {
        message.error("Please select a role for the user.");
        return;
      }

      // Check if this is a duplicate email error
      if (checkForDuplicateEmail(error)) {
        message.error(
          "Email address already exists. Please use a different email."
        );
      } else {
        message.error("Failed to create user. Please try again.");
      }
    },
  });
};

// ✅ Mutation for Updating a User
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      userData,
    }: {
      userId: number;
      userData: {
        name: string;
        email: string;
        password?: string;
        user_type_id: number;
        role_id?: number;
        location_id: number;
        date_of_birth?: string;
        gender: string;
        country: string;
        state: string;
        lga: string;
        address?: string;
        phone: string;
        whatsapp_phone?: string;
        avatar?: string;
        status: boolean;
      };
    }) => {
      // Make sure role_id is not undefined or null
      if (!userData.role_id) {
        throw new Error("Role ID is required");
      }

      // Create a new payload with proper handling for password and role_id
      const payload = { ...userData };

      // Ensure role_id is a number
      payload.role_id = Number(payload.role_id);

      // Remove password if it's empty
      if (!payload.password) {
        delete payload.password;
      }

      console.log("Updating user with payload:", payload); // For debugging

      const response = await apiClient.put(`/user/${userId}`, payload);
      return response.data;
    },
    onSuccess: () => {
      message.success("User updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: any) => {
      console.error("Update user error:", error);

      // Check for role_id errors
      if (error?.message === "Role ID is required") {
        message.error("Please select a role for the user.");
        return;
      }

      // Check if this is a duplicate email error
      if (checkForDuplicateEmail(error)) {
        message.error(
          "Email address already exists. Please use a different email."
        );
      } else {
        message.error("Failed to update user. Please try again.");
      }
    },
  });
};

// ✅ Fetch All User Types Hook (returns a map)
export const useUserTypes = () => {
  return useQuery({
    queryKey: ["userTypes"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { id: number; name: string }[];
      }>("/usertype");
      return response.data.data.reduce((acc: Record<number, string>, type) => {
        acc[type.id] = type.name;
        return acc;
      }, {}); // ✅ Returns a map { 1: "Admin", 2: "Staff", ... }
    },
    staleTime: 1000 * 60 * 10, // ✅ Cache for 10 minutes
  });
};

// ✅ Fetch **Specific** User Type Hook
export const useUserType = (userTypeId?: number) => {
  return useQuery({
    queryKey: ["userType", userTypeId],
    queryFn: async () => {
      if (!userTypeId) return null;
      const response = await apiClient.get<{ data: { name: string } }>(
        `/usertype/${userTypeId}`
      );
      return response.data.data.name; // ✅ Extract only the name
    },
    enabled: !!userTypeId, // ✅ Fetch only if `userTypeId` exists
    staleTime: 1000 * 60 * 10,
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await apiClient.delete(`/user/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("User deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["users"] }); // Invalidate the users query to refetch data
    },
    onError: () => {
      message.error("Failed to delete user. Please try again.");
    },
  });
};

// ✅ Location API Response Type
interface Location {
  id: number;
  name: string;
  address: string;
  code: string;
}

// ✅ Fetch All Locations (For Super Admin)
export const useLocations = () => {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const response = await apiClient.get<{
        status: boolean;
        data: Location[];
      }>("/location");
      return response.data.data; // ✅ Extract location data
    },
    staleTime: 1000 * 60 * 60, // ✅ Cache for 1 hour
    refetchOnWindowFocus: false,
  });
};

// ✅ Fetch Single User Location
export const useUserLocation = (locationId?: number) => {
  return useQuery({
    queryKey: ["userLocation", locationId],
    queryFn: async () => {
      if (!locationId) return null;
      const response = await apiClient.get<{ status: boolean; data: Location }>(
        `/location/${locationId}`
      );
      return response.data.data.name; // ✅ Extract location name
    },
    enabled: !!locationId, // ✅ Only run if locationId exists
  });
};

// ✅ Mutation Hook for Updating User Location
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: number) => {
      await apiClient.put(`/location/${locationId}`, { id: locationId }); // ✅ Send PUT request
    },
    onSuccess: () => {
      message.success("Location updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["currentUser"] }); // ✅ Refresh user data
      queryClient.invalidateQueries({ queryKey: ["userLocation"] }); // ✅ Refresh location data
    },
    onError: () => {
      message.error("Failed to update location");
    },
  });
};
